import FormHelperText from "@mui/material/FormHelperText";
import { observer } from "mobx-react-lite";
import type React from "react";
import FormControl from "./FormControl";

export type InputWrapperArgs = {
  label?: string;
  children: React.ReactNode;
  value?: unknown;
  error?: boolean;
  helperText?: string | null;
  maxLength?: number;
  disabled?: boolean;
  actions?: React.ReactNode;
  inline?: boolean;
  explanation?: React.ReactNode;
  "data-test-id"?: string;
  required?: boolean;
  flexWrap?: "nowrap" | "wrap" | "wrap-reverse" | "initial" | "inherit";
};

function InputWrapper({
  label,
  children,
  value = {},
  error,
  helperText,
  maxLength,
  disabled = false,
  actions = <div></div>,
  inline = false,
  explanation,
  "data-test-id": dataTestId,
  required,
  flexWrap,
}: InputWrapperArgs): React.ReactNode {
  /*
   * We pass `value` as `mightBeString`, rather than pulling from props, so
   * that the information about whether it is a string or not is preserved by
   * the type checker. See flow docs on `%checks`.
   */
  function showCharacterCount(mightBeString: unknown): mightBeString is string {
    return !disabled && typeof maxLength === "number" && typeof mightBeString === "string";
  }

  return (
    <FormControl
      label={label}
      inline={inline}
      actions={actions}
      data-test-id={dataTestId}
      required={required}
      error={error}
      explanation={explanation}
      flexWrap={flexWrap}
      disabled={disabled}
    >
      {children}
      {!error && showCharacterCount(value) && (
        <FormHelperText component="div">{`${value.length} / ${maxLength ?? ""}`}</FormHelperText>
      )}
      {error && showCharacterCount(value) && (
        <FormHelperText error component="div">
          {helperText ?? `No more than ${maxLength ?? ""} characters permitted.`}
        </FormHelperText>
      )}
      {error && !showCharacterCount(value) && (
        <FormHelperText error component="div">
          {helperText ?? ""}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export default observer(InputWrapper);
